import {
    formatCasesAndLooseSummary,
    resolveReceivedDisplayFields,
} from 'components/warehouse/components/fcTransfers/acceptTransfer/acceptTransferLineDisplayUtils';
import dayjs from 'utils/dayjsConfig';

/**
 * Evenly split full cases across pallets; put all loose units on the last pallet.
 *
 * @param {number} totalCases
 * @param {number} totalLoose
 * @param {number} palletCount
 * @returns {Array<{ numFullCases: number, numLooseUnits: number }>}
 */
export function distributeCasesAndLooseAcrossPallets(totalCases, totalLoose, palletCount) {
    const n = Math.max(1, Math.floor(Number(palletCount)) || 1);
    const cases = Math.max(0, Math.trunc(Number(totalCases)) || 0);
    const loose = Math.max(0, Math.trunc(Number(totalLoose)) || 0);

    const base = Math.floor(cases / n);
    const remainder = cases % n;
    /** @type {Array<{ numFullCases: number, numLooseUnits: number }>} */
    const rows = [];
    for (let i = 0; i < n; i += 1) {
        const palletCases = base + (i < remainder ? 1 : 0);
        const palletLoose = i === n - 1 ? loose : 0;
        rows.push({ numFullCases: palletCases, numLooseUnits: palletLoose });
    }
    return rows;
}

/**
 * @param {Record<string, unknown>} item
 * @returns {{ cases: number, loose: number }}
 */
export function getExpectedCasesAndLoose(item) {
    const rawCases =
        item?.expectedNumCases ??
        item?.expected_num_cases ??
        item?.numCases ??
        item?.num_cases ??
        item?.shippedNumCases ??
        0;
    const rawLoose =
        item?.expectedNumLooseUnits ??
        item?.expected_num_loose_units ??
        item?.numLooseUnits ??
        item?.num_loose_units ??
        item?.shippedNumLooseUnits ??
        0;
    const cases = Math.max(0, Math.trunc(Number(rawCases)) || 0);
    const loose = Math.max(0, Math.trunc(Number(rawLoose)) || 0);
    return { cases, loose };
}

/**
 * @param {unknown} raw
 * @returns {Array<{ numFullCases: string, numLooseUnits: string }> | null}
 */
function parseExistingReceivedQuantities(raw) {
    if (!Array.isArray(raw) || raw.length === 0) {
        return null;
    }
    /** @type {Array<{ numFullCases: string, numLooseUnits: string }>} */
    const out = [];
    for (const row of raw) {
        if (!row || typeof row !== 'object') {
            return null;
        }
        const rec = /** @type {Record<string, unknown>} */ (row);
        const nc = rec.numFullCases ?? rec.num_full_cases ?? rec.numCases ?? rec.num_cases;
        const nl = rec.numLooseUnits ?? rec.num_loose_units;
        out.push({
            numFullCases: nc === undefined || nc === null || nc === '' ? '' : String(nc),
            numLooseUnits: nl === undefined || nl === null || nl === '' ? '' : String(nl),
        });
    }
    return out.length ? out : null;
}

/**
 * Initial receipt draft for the save-line payload (tech spec §5.3).
 *
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
export function buildInitialDraftFromItem(item) {
    const { casesRaw, expiresOnRaw, looseRaw, unitsPerCaseRaw } = resolveReceivedDisplayFields(item);
    const rawExpires = expiresOnRaw ?? '';
    let receivedExpiresOn = '';
    if (rawExpires !== undefined && rawExpires !== null && rawExpires !== '') {
        const s = String(rawExpires).trim();
        receivedExpiresOn = s.length >= 10 ? s.slice(0, 10) : s;
    }

    const rawUpc = unitsPerCaseRaw;
    const receivedUnitsPerCase = rawUpc === undefined || rawUpc === null || rawUpc === '' ? '' : String(rawUpc);

    const existingQty =
        parseExistingReceivedQuantities(item?.receivedQuantities) ??
        parseExistingReceivedQuantities(item?.received_quantities);

    const { cases: expectedCases, loose: expectedLoose } = getExpectedCasesAndLoose(item);
    const hasResolvedReceivedQty =
        (casesRaw !== undefined && casesRaw !== null && casesRaw !== '') ||
        (looseRaw !== undefined && looseRaw !== null && looseRaw !== '');
    const defaultCases = hasResolvedReceivedQty ? Math.max(0, Math.trunc(Number(casesRaw)) || 0) : expectedCases;
    const defaultLoose = hasResolvedReceivedQty ? Math.max(0, Math.trunc(Number(looseRaw)) || 0) : expectedLoose;

    let numPallets = '1';
    /** @type {Array<{ numFullCases: string, numLooseUnits: string }>} */
    let receivedQuantities;

    if (existingQty) {
        numPallets = String(existingQty.length);
        receivedQuantities = existingQty;
    } else {
        const distributed = distributeCasesAndLooseAcrossPallets(defaultCases, defaultLoose, 1);
        receivedQuantities = distributed.map((row) => ({
            numFullCases: String(row.numFullCases),
            numLooseUnits: String(row.numLooseUnits),
        }));
    }

    return {
        receivedExpiresOn,
        receivedUnitsPerCase,
        numPallets,
        receivedQuantities,
    };
}

/**
 * Resize pallet rows to match `newCount`, redistributing expected cases/loose using the same rules as initial seed.
 *
 * @param {number} expectedCases
 * @param {number} expectedLoose
 * @param {number} newCount
 * @returns {Array<{ numFullCases: string, numLooseUnits: string }>}
 */
export function receivedQuantitiesForPalletCount(expectedCases, expectedLoose, newCount) {
    const distributed = distributeCasesAndLooseAcrossPallets(expectedCases, expectedLoose, newCount);
    return distributed.map((row) => ({
        numFullCases: String(row.numFullCases),
        numLooseUnits: String(row.numLooseUnits),
    }));
}

/**
 * Expected values for mismatch warnings (aligned with accept-transfer display helpers).
 *
 * @param {Record<string, unknown> | null | undefined} item
 * @returns {{ expectedExpiresOn: string, expectedUnitsPerCase: number | null, expectedCases: number, expectedLoose: number }}
 */
export function getExpectedReceiptSnapshot(item) {
    const { cases, loose } = getExpectedCasesAndLoose(item ?? {});
    const rawBbd =
        item?.expectedExpiresOn ??
        item?.expected_expires_on ??
        item?.sourceExpiresOnSnapshot ??
        item?.source_expires_on_snapshot ??
        item?.effectiveExpiresOn;
    let expectedExpiresOn = '';
    if (rawBbd !== undefined && rawBbd !== null && rawBbd !== '') {
        const s = String(rawBbd).trim();
        expectedExpiresOn = s.length >= 10 ? s.slice(0, 10) : '';
    }

    const rawUpc =
        item?.expectedUnitsPerCase ??
        item?.expected_units_per_case ??
        item?.sourceUnitsPerCaseSnapshot ??
        item?.source_units_per_case_snapshot ??
        item?.effectiveUnitsPerCase;
    let expectedUnitsPerCase = null;
    if (rawUpc !== undefined && rawUpc !== null && rawUpc !== '') {
        const n = Number(rawUpc);
        if (Number.isFinite(n)) {
            expectedUnitsPerCase = Math.trunc(n);
        }
    }

    return {
        expectedExpiresOn,
        expectedUnitsPerCase,
        expectedCases: cases,
        expectedLoose: loose,
    };
}

/**
 * Non-blocking warnings when entered receipt fields differ from expected line snapshot.
 *
 * @param {Record<string, unknown>} draft
 * @param {ReturnType<typeof getExpectedReceiptSnapshot>} expected
 * @returns {Array<{ id: string, message: string }>}
 */
export function getReceiptMismatchWarnings(draft, expected) {
    /** @type {Array<{ id: string, message: string }>} */
    const warnings = [];

    const actualBbd = String(draft?.receivedExpiresOn ?? '').trim();
    if (expected.expectedExpiresOn && (!actualBbd || actualBbd !== expected.expectedExpiresOn)) {
        warnings.push({
            id: 'bbd',
            message: `Best-by date does not match expected (${expected.expectedExpiresOn}).`,
        });
    }

    const actualUpc = Math.trunc(Number(draft?.receivedUnitsPerCase)) || 0;
    if (expected.expectedUnitsPerCase !== null && actualUpc > 0 && actualUpc !== expected.expectedUnitsPerCase) {
        warnings.push({
            id: 'unitsPerCase',
            message: `Count per case does not match expected (${expected.expectedUnitsPerCase}).`,
        });
    }

    const rows = Array.isArray(draft?.receivedQuantities) ? draft.receivedQuantities : [];
    let sumCases = 0;
    let sumLoose = 0;
    for (const row of rows) {
        if (row && typeof row === 'object') {
            const rec = /** @type {Record<string, unknown>} */ (row);
            sumCases += Math.trunc(Number(rec.numFullCases)) || 0;
            sumLoose += Math.trunc(Number(rec.numLooseUnits)) || 0;
        }
    }
    if (sumCases !== expected.expectedCases || sumLoose !== expected.expectedLoose) {
        warnings.push({
            id: 'quantities',
            message: `Received totals (${formatCasesAndLooseSummary(sumCases, sumLoose)}) do not match expected (${formatCasesAndLooseSummary(expected.expectedCases, expected.expectedLoose)}).`,
        });
    }

    return warnings;
}

/**
 * @param {Record<string, unknown>} draft
 * @returns {string | null} - `null` when valid
 */
export function validateAcceptTransferLineDraft(draft) {
    if (!draft || typeof draft !== 'object') {
        return 'Missing draft values';
    }

    const upc = Number(draft.receivedUnitsPerCase);
    if (!Number.isFinite(upc) || upc <= 0 || !Number.isInteger(upc)) {
        return 'Units per case must be a whole number greater than zero';
    }

    const palletNum = Number(draft.numPallets);
    if (!Number.isFinite(palletNum) || palletNum < 1 || !Number.isInteger(palletNum)) {
        return 'Number of pallets must be a whole number of at least 1';
    }

    const rows = draft.receivedQuantities;
    if (!Array.isArray(rows) || rows.length === 0) {
        return 'At least one pallet row is required';
    }
    if (rows.length !== palletNum) {
        return 'Pallet rows must match the number of pallets';
    }

    for (const row of rows) {
        if (!row || typeof row !== 'object') {
            return 'Invalid pallet row';
        }
        const rec = /** @type {Record<string, unknown>} */ (row);
        const cases = Number(rec.numFullCases);
        const loose = Number(rec.numLooseUnits);
        if (!Number.isFinite(cases) || cases < 0 || !Number.isInteger(cases)) {
            return 'Cases must be a non-negative whole number';
        }
        if (!Number.isFinite(loose) || loose < 0 || !Number.isInteger(loose)) {
            return 'Loose units must be a non-negative whole number';
        }
    }

    const bbd = String(draft.receivedExpiresOn ?? '').trim();
    if (bbd.length > 0) {
        const parsed = dayjs(bbd, 'YYYY-MM-DD', true);
        if (!parsed.isValid()) {
            return 'Best-by date must be YYYY-MM-DD';
        }
    }

    return null;
}

/**
 * Body fragment accepted by `saveFCTransferReceiptLine` before API normalization.
 *
 * @param {Record<string, unknown>} draft
 * @returns {{ receivedExpiresOn: string | null, receivedUnitsPerCase: number, receivedQuantities: Array<{ numFullCases: number, numLooseUnits: number }> }}
 */
export function buildReceiptLinePayloadFromDraft(draft) {
    const trimmed = String(draft?.receivedExpiresOn ?? '').trim();
    const rows = Array.isArray(draft?.receivedQuantities) ? draft.receivedQuantities : [];
    const receivedQuantities = rows.map((row) => {
        const rec = row && typeof row === 'object' ? /** @type {Record<string, unknown>} */ (row) : {};
        return {
            numFullCases: Math.trunc(Number(rec.numFullCases)) || 0,
            numLooseUnits: Math.trunc(Number(rec.numLooseUnits)) || 0,
        };
    });
    return {
        receivedExpiresOn: trimmed.length ? trimmed : null,
        receivedUnitsPerCase: Math.trunc(Number(draft?.receivedUnitsPerCase)) || 0,
        receivedQuantities,
    };
}
